"use client";

import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { fetchSubscriptionUsageReport } from "@/store/modules/SubscriptionsAdmin/thunks";
import { clearUsageReport } from "@/store/modules/SubscriptionsAdmin/slice";
import { useApiReady } from "@/hooks";

interface UseSubscriptionUsageProps {
  defaultDateRange?: {
    start: Date;
    end: Date;
  };
}

const useSubscriptionUsage = ({
  defaultDateRange,
}: UseSubscriptionUsageProps = {}) => {
  // Data padrão: início do mês atual até hoje
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const [dateRange, setDateRange] = useState({
    start: defaultDateRange?.start || startOfMonth,
    end: defaultDateRange?.end || now,
  });

  const [filters, setFilters] = useState({
    client_id: "",
    subscription_id: "",
    status: "",
  });

  const dispatch: AppDispatch = useDispatch();
  const { isReady: isApiReady } = useApiReady(2000);

  // Selectors
  const usageReport = useSelector(
    (state: RootState) => state.subscription.usageReport,
  );
  const loading = useSelector(
    (state: RootState) => state.subscription.loadingUsageReport,
  );
  const error = useSelector((state: RootState) => state.subscription.error);

  // Função para buscar dados do relatório
  const fetchUsageReport = async (customParams?: Record<string, any>) => {
    const params: any = {
      start_date: dateRange.start.toISOString().split("T")[0],
      end_date: dateRange.end.toISOString().split("T")[0],
      ...filters,
      ...customParams,
    };

    // Remove parâmetros vazios
    Object.keys(params).forEach((key) => {
      if (
        params[key] === "" ||
        params[key] === null ||
        params[key] === undefined
      ) {
        delete params[key];
      }
    });

    return dispatch(fetchSubscriptionUsageReport({ params }));
  };

  // Função para atualizar filtros
  const updateFilters = (newFilters: Partial<typeof filters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Função para atualizar período
  const updateDateRange = (newRange: { start: Date; end: Date }) => {
    setDateRange(newRange);
  };

  // Função para limpar dados
  const clearData = () => {
    dispatch(clearUsageReport());
  };

  // Função para aplicar filtros
  const applyFilters = (options?: { page?: number }) => {
    const page = options?.page;
    const paginationParams =
      page && page > 0
        ? {
            page,
            current_page: page,
          }
        : undefined;

    fetchUsageReport(paginationParams);
  };

  // Função para resetar filtros
  const resetFilters = () => {
    setFilters({
      client_id: "",
      subscription_id: "",
      status: "",
    });
    setDateRange({
      start: startOfMonth,
      end: now,
    });
  };

  // Carregar dados iniciais
  useEffect(() => {
    if (!isApiReady) return;
    fetchUsageReport();

    // Cleanup ao desmontar o componente
    return () => {
      clearData();
    };
  }, [isApiReady]); // Só executa quando API estiver pronta

  const usagePayload = useMemo(() => {
    if (!usageReport) {
      return null;
    }
    return usageReport.data ?? usageReport;
  }, [usageReport]);

  const usageItems = useMemo(() => {
    const rawItems = usagePayload?.usage_data;

    if (Array.isArray(rawItems)) {
      return rawItems;
    }

    if (rawItems && Array.isArray(rawItems.data)) {
      return rawItems.data;
    }

    return [];
  }, [usagePayload]);

  const usagePagination = useMemo(() => {
    const rawItems = usagePayload?.usage_data;

    if (!rawItems) {
      return null;
    }

    const meta = rawItems.meta || rawItems.pagination || rawItems;
    const total = meta?.total ?? rawItems.total ?? usageItems.length;
    const perPage =
      (meta?.per_page ??
        meta?.perPage ??
        rawItems.per_page ??
        usageItems.length) ||
      10;
    const currentPage =
      meta?.current_page ?? meta?.currentPage ?? rawItems.current_page ?? 1;
    const lastPage =
      meta?.last_page ??
      meta?.lastPage ??
      meta?.total_pages ??
      meta?.pages ??
      rawItems.total_pages ??
      Math.max(1, Math.ceil(total / (perPage || 1)));

    return {
      total,
      perPage,
      currentPage,
      totalPages: lastPage,
    };
  }, [usagePayload, usageItems.length]);

  const handlePageChange = (page: number) => {
    const requestedPage = Number.isFinite(page) ? Math.trunc(page) : 1;
    const minPage = 1;
    const maxPage = usagePagination?.totalPages ?? Math.max(requestedPage, 1);
    const safePage = Math.min(Math.max(requestedPage, minPage), maxPage);

    applyFilters({ page: safePage });
  };

  const finalizedItems = useMemo(() => {
    const finalizedStatuses = ["finalized", "completed", "finished"];
    return usageItems.filter((item: any) =>
      finalizedStatuses.includes(String(item?.status || "").toLowerCase()),
    );
  }, [usageItems]);

  const computedSummary = useMemo(() => {
    if (!usagePayload) {
      return null;
    }

    const baseSummary = usagePayload.summary ?? {};
    const totalFinalized = finalizedItems.length;
    const totalEntries = usageItems.length;

    const totalPoints = finalizedItems.reduce((acc: any, item: any) => {
      const points = Number(item?.points ?? 0);
      return acc + (Number.isFinite(points) ? points : 0);
    }, 0);

    const subscriberIdentifiers = finalizedItems.map((item: any) => {
      if (item?.client?.id != null) return String(item.client.id);
      if (item?.client?.slug) return String(item.client.slug);
      if (item?.client?.document) return String(item.client.document);
      return String(item?.client?.name ?? "");
    });

    const activeSubscriberCount = Array.from(
      new Set(subscriberIdentifiers.filter((value: any) => value !== "")),
    ).length;

    const averageUsagePerSubscriber =
      activeSubscriberCount > 0
        ? Number((totalFinalized / activeSubscriberCount).toFixed(2))
        : 0;

    const averagePointsPerUsage =
      totalFinalized > 0
        ? Number((totalPoints / totalFinalized).toFixed(2))
        : 0;

    const completionRate =
      totalEntries > 0
        ? `${Math.round((totalFinalized / totalEntries) * 100)}%`
        : "0%";

    return {
      ...baseSummary,
      total_usages: totalFinalized,
      active_subscribers: activeSubscriberCount,
      average_usage_per_subscriber: averageUsagePerSubscriber,
      completion_rate: completionRate,
      total_points: totalPoints,
      average_points_per_usage: averagePointsPerUsage,
    };
  }, [usagePayload, finalizedItems, usageItems]);

  const normalizedReportData = useMemo(() => {
    if (!usagePayload) {
      return null;
    }

    const summaryData = computedSummary ?? usagePayload.summary ?? null;

    const details =
      usageReport?.details ??
      usagePayload.details ??
      usageReport?.data?.details ??
      null;

    const period =
      usagePayload.period ??
      usageReport?.period ??
      usageReport?.data?.period ??
      null;

    return {
      ...usagePayload,
      summary: summaryData,
      usage_data: usageItems,
      finalized_usage_data: finalizedItems,
      details,
      period,
    };
  }, [usagePayload, usageReport, computedSummary, usageItems, finalizedItems]);

  return {
    // Estado
    usageReport,
    loading,
    error,
    dateRange,
    filters,

    // Funções
    fetchUsageReport,
    updateFilters,
    updateDateRange,
    clearData,
    applyFilters,
    resetFilters,
    handlePageChange,

    // Dados processados (se necessário)
    reportData: normalizedReportData || {
      usage_data: [],
      summary: computedSummary,
      finalized_usage_data: finalizedItems,
    },
    summary: computedSummary ?? usagePayload?.summary ?? null,
    pagination: usagePagination,
    totalRecords: usagePagination?.total ?? usageItems.length,
  };
};

export default useSubscriptionUsage;
